import { useQuery } from 'react-query';
import toastr from 'react-hot-toast';
import { fetchWrapper } from 'utils/fetchWrapper';
import { CACHE_AND_STALE_TIME } from 'constants/index';
import React from 'react';
import { generateFilter, generateURL } from 'utilities/hooks/urlGeneration';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import type { AccountUser, AccountUserResponse } from 'types/AccountUser';

const getAccountUsers = (accountID: string | undefined | null) => {
  if (!accountID) {
    Promise.reject('AccountID can not be null');
    return;
  }
  const queryArray = [generateFilter('accountID', accountID)];
  const url = generateURL(SPARK_RESOURCE_TYPES.ACCOUNT_USERS, queryArray);
  return fetchWrapper<AccountUserResponse>({
    url: url,
    method: 'GET',
  });
};

export function useAccountUsers(accountID: string | undefined | null) {
  const { data, ...rest } = useQuery(
    'AccountUsers',
    () => getAccountUsers(accountID),
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`Failed to fetch Account Users: ${error.message}`);
        }
      },
      enabled: Boolean(accountID),
      ...CACHE_AND_STALE_TIME,
    }
  );

  const { data: responseData, ...restData } = data || {};

  const idMap = responseData?.reduce<Record<string, AccountUser>>(
    (acc, curr) => {
      return { ...acc, [curr.id]: curr };
    },
    {}
  );

  const accountIDbyUserIdMap = React.useMemo(() => {
    return responseData?.reduce<Record<string, string>>((acc, curr) => {
      return { ...acc, [curr.attributes.userID]: curr.attributes.accountID };
    }, {});
  }, [responseData]);

  return {
    accountUsers: responseData || [],
    accountIDbyUserIdMap,
    idMap,
    ...restData,
    ...rest,
  };
}
